import type { FormConfig } from "@amzn/aquila-feedback-web-assets";
import { FormType } from "@amzn/aquila-feedback-web-assets";
import type { AsyncStatus, UseAsyncActions } from "@react-hookz/web";
import { useAsync } from "@react-hookz/web";
import { useEffect } from "react";
import { useStore } from "zustand";

import { getAquilaApiClient } from "../../../../api/aquilaApi/aquilaApi";
import {
    notificationStore,
    NotificationType,
} from "../../../../store/notification";
import { reportIssueStore } from "../../../../store/reportIssue";

interface GetReportIssueFormProps {
    reportIssueFormConfig: { configId: string; awsAccountId: string };
}

export const useGetReportIssueForm = ({
    reportIssueFormConfig,
}: GetReportIssueFormProps): [
    FormConfig,
    AsyncStatus,
    UseAsyncActions<void, []>,
] => {
    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);
    const [formConfigMap, setFormConfig] = useStore(
        reportIssueStore,
        (state) => [state.formConfigMap, state.setFormConfig],
    );

    const [getFormConfigAsyncState, getFormConfigAsyncAction] = useAsync(
        async () => {
            if (!reportIssueFormConfig.configId) {
                throw new Error("Report issue form config is not provided");
            }
            const response = await (
                await getAquilaApiClient()
            ).getFeedbackFormConfig(reportIssueFormConfig);
            setFormConfig(reportIssueFormConfig.configId, {
                type: FormType.MODAL,
                header: response.data.header ?? [],
                body: response.data.body ?? [],
            });
        },
    );

    useEffect(() => {
        if (getFormConfigAsyncState.status === "error") {
            console.error(getFormConfigAsyncState.error);
            upsertNotification(
                "Failed to load report issue form. Please try again later.",
                NotificationType.ERROR,
                "getReportIssueFormError",
            );
        }
    }, [getFormConfigAsyncState.status]);

    return [
        formConfigMap[reportIssueFormConfig.configId],
        getFormConfigAsyncState.status,
        getFormConfigAsyncAction,
    ];
};
